// Contactos — the directory of people who have written to the business. Row →
// the unified contact detail. Composes the locked design system; es-MX copy.

import { useQuery } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'

import { type ContactSummary, getContacts } from './contacts-data.js'
import {
  Chip,
  DataTable,
  type DataTableColumn,
  EmptyStateCard,
  Input,
  PageHeader,
  Spinner,
} from '../../design-system/index.js'
import { QueryError } from '../../lib/QueryError.js'

const fmtDate = (iso: string): string =>
  new Date(iso).toLocaleDateString('es-MX', { day: 'numeric', month: 'short', year: 'numeric' })

export function ContactsPage(): JSX.Element {
  const [q, setQ] = useState('')
  // A6: el término ya reposado. Se pedía /contacts en cada tecla, y como la
  // clave de la consulta lleva el término, una respuesta vieja podía pintarse
  // encima de la nueva. Con el debounce solo hay una consulta activa a la vez.
  const [term, setTerm] = useState('')

  useEffect(() => {
    const timer = setTimeout(() => setTerm(q.trim()), 250)
    return () => clearTimeout(timer)
  }, [q])

  const {
    data: items,
    isLoading,
    isError,
    refetch,
  } = useQuery({
    queryKey: ['contacts', term],
    queryFn: () => getContacts(term || undefined),
  })

  const columns: DataTableColumn<ContactSummary>[] = [
    {
      key: 'name',
      label: 'Contacto',
      render: (c) => (
        <Link to={`/contactos/${c.id}`} className="font-medium text-link hover:underline">
          {c.displayName}
        </Link>
      ),
    },
    { key: 'phone', label: 'Teléfono', render: (c) => c.phone },
    {
      key: 'tags',
      label: 'Etiquetas',
      render: (c) =>
        c.tags.length > 0 ? (
          <div className="flex flex-wrap gap-1">
            {c.tags.map((t) => (
              <Chip key={t} tone="info">
                {t}
              </Chip>
            ))}
          </div>
        ) : (
          '—'
        ),
    },
    {
      key: 'lastSeen',
      label: 'Último contacto',
      align: 'right',
      render: (c) => fmtDate(c.lastSeen),
    },
  ]

  return (
    <div data-testid="contacts" className="mx-auto w-full max-w-5xl">
      <PageHeader title="Contactos" subtitle="Las personas que han escrito a tu negocio." />
      <div className="mb-4 max-w-sm">
        <Input
          placeholder="Buscar por nombre o teléfono…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          aria-label="Buscar contactos"
        />
      </div>
      {isError ? (
        <QueryError message="No pudimos cargar tus contactos." onRetry={() => void refetch()} />
      ) : isLoading || !items ? (
        <Spinner label="Cargando contactos…" />
      ) : items.length === 0 ? (
        <EmptyStateCard
          icon={<span aria-hidden>👤</span>}
          kind="day1"
          title="Sin contactos aún"
          body="Cuando alguien escriba a tu WhatsApp, aparecerá aquí."
        />
      ) : (
        <DataTable caption="Contactos" columns={columns} rows={items} />
      )}
    </div>
  )
}
